import type { BillingSummary } from '@chatsdet/types';

type MeterLevel = 'ok' | 'warning' | 'full';

export type Meter = {
  /** Used plus queued (executions) or in-flight (agent hours); this is what counts toward the limit. */
  counted: number;
  included: number | null;
  /** Null when unlimited. Not capped at 100. */
  percent: number | null;
  level: MeterLevel;
};

/** Warn at 80% and at 100% (spec D10). `included: null` is unlimited. */
export function meterOf(used: number, pending: number, included: number | null): Meter {
  const counted = used + pending;
  if (included === null) return { counted, included, percent: null, level: 'ok' };
  const percent = included === 0 ? 100 : (counted / included) * 100;
  return { counted, included, percent, level: percent >= 100 ? 'full' : percent >= 80 ? 'warning' : 'ok' };
}

export function billingMeters(summary: BillingSummary) {
  const { executions, agentHours } = summary;
  return {
    executions: meterOf(executions.used, executions.queued, executions.included),
    agentHours: meterOf(agentHours.used, agentHours.inFlight, agentHours.included),
  };
}

const dayFormat = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' });

/** A billing date as its UTC day; the API stores trial ends and periods as UTC. */
export const formatDay = (iso: string) => dayFormat.format(new Date(iso));

/** A trial ended before it began has an empty period; the agent-time route rejects it. */
export const isEmptyPeriod = (period: { start: string; end: string }) =>
  new Date(period.start).getTime() >= new Date(period.end).getTime();

/**
 * Time left on a trial (or since it ended), as the trial banner and the superadmin
 * Trial column both show it. One calculation so the two never round differently:
 * whole days, else whole hours, else minutes, always rounded down.
 */
export function formatTrialRemaining(endsAt: Date, now: Date): { text: string; expired: boolean } {
  const diffMs = endsAt.getTime() - now.getTime();
  const expired = diffMs <= 0;
  const absMs = Math.abs(diffMs);
  const days = Math.floor(absMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((absMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60));

  let text: string;
  if (days >= 1) {
    text = `${days} day${days === 1 ? '' : 's'}`;
  } else if (hours >= 1) {
    text = `${hours} hour${hours === 1 ? '' : 's'}`;
  } else {
    text = `${Math.max(minutes, 0)} minute${minutes === 1 ? '' : 's'}`;
  }
  return { text, expired };
}
